import React, { JSX, useEffect, useId } from "react";
import { resizeObserverManager } from "@app/shared/qes-standards/utility/ResizeObserverManager";
import cx from "classnames";
import { ColumnConfig, ColumnGroup, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { PCAPayerParticipation } from "@app/projects/results/servicing-provider/servicingProviderDetail";
import { WafflesThemes } from "@app/shared/waffles/exports";
import { ClaimsActivityLineOfBusinessTypeEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EllipsisTooltip } from "@app/shared/EllipsisTooltip";


interface LineOfBusinessPayerMixProps {
  lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum;
  participationDisplay?: PCAPayerParticipation[];
  className?: string;
  testId: string;
}

interface ClaimsProviderInsightsWaffleToppings {
  [wafflesKeyColumnName]: string;
  payer: JSX.Element;
  percent: string;
}

/**
 * Line of Business Payer Mix Component
 * This component displays the payer mix for a specific line of business
 */
const LineOfBusinessPayerMix = (props: LineOfBusinessPayerMixProps): JSX.Element => {
  const lobPayerMixId = useId();
  const lobPercentColumnTestId = `${props.lineOfBusiness}-percent`;
  const lobPayerColumnTestId = `${props.lineOfBusiness}-payer`
  const [payerWidth, setPayerWidth] = React.useState<number>(0);

  useEffect(() => {
    const resizeCallback = (event: ResizeObserverEntry): void => {
      if (!!event) {
        const width = event?.contentBoxSize[0].inlineSize
        setPayerWidth(width);
      }
    }

    const payerMix = document.getElementById(lobPayerMixId);

    if (!!payerMix) {
      resizeObserverManager.observe(payerMix, resizeCallback);

      return () => {
        resizeObserverManager.unobserve(payerMix);
      }
    }
  }, []);

  const getColumnGroups = (): ColumnGroup<ClaimsProviderInsightsWaffleToppings>[] => {
    return (
      [
        {
          children: ["payer", "percent"],
          key: '', /*no longer to show the title*/
          testId: props.lineOfBusiness.toLowerCase(),
        },
      ]
    );
  }

  const getColumnConfig = (): ColumnConfig => {
    return ({
      payer: {
        displayText: "Payer",
        noResize: true,
        noSort: true,
        sizeContentToFit: true,
        testId: lobPayerColumnTestId,
      },
      percent: {
        displayText: "Percent",
        minWidth: "95px",
        noResize: true,
        noSort: true,
        sizeContentToFit: true,
        testId: lobPercentColumnTestId,
      },
    });
  };

  const getData = (data: PCAPayerParticipation[]): ClaimsProviderInsightsWaffleToppings[] => {
    if (!data) {
      return [];
    }

    if (data.length === 0) {
      // adding a fake row with "---" as the values in order to depict no data
      // instead of the giant default "no records..." waffles text
      data = [{
        claimsVolume: null,
        payerName: "---",
      }];
    }

    return data.map((x, index) => {
      /* oxlint-disable sort-keys */
      return {
        [wafflesKeyColumnName]: index.toString(),
        percent: x.claimsVolume ? `${x.claimsVolume.toFixed(1)}%` : "---",
        payer: (
          <EllipsisTooltip
            width={payerWidth ? `${payerWidth/2}px` : "auto"}
            text={x.payerName}
          />
        ),
      };
      /* oxlint-enable sort-keys */
    });
  }

  return (
    <div
      className={cx({ [props.className]: !!props.className, "data-box": true })}
      id={lobPayerMixId}
    >
      <div className="data-box-header">
        {props.lineOfBusiness}
      </div>
      <div className="data-box-content" key={`${props.lineOfBusiness}-0`}>
        <Waffles<ClaimsProviderInsightsWaffleToppings>
          columns={getColumnConfig()}
          columnGroups={getColumnGroups()}
          data={getData(props.participationDisplay) || []}
          fullWidth={true}
          testId={props.testId}
          theme={WafflesThemes.Flyout}
        />
      </div>
    </div>
  );
};

export default LineOfBusinessPayerMix;